import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Empty } from 'antd';
import { AppPagination, AppTable, AppButton, AppTooltip } from '@/components/atoms';
import { EditFill, Delete } from '@/components/atoms/AppIcon';
import { EMPTY_TEXT_FILTERED } from '@/constants';
import { getServiceTypeShortLabel } from '@/constants/serviceType';
import { ADMIN_ROUTES } from '@/constants/routes';
import { useStandardDeleteConfirm } from '@/hooks/useStandardDeleteConfirm';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import type { AdminInformationItem } from '@/services/admin/informations';
import { renderTableCellTruncate } from '@/utils/tableCellTruncate';
import { convertWarekiDetail } from '@/utils/wareki';

type SortDir = 'ascend' | 'descend';
type SortField = 'informations.id' | 'informations.information_date';

type InformationsMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

type InformationsTableSectionProps = {
  showSiteTypeColumn: boolean;
  rows: AdminInformationItem[];
  loading: boolean;
  meta: InformationsMeta;
  defaultPerPage: number;
  onSort: (field: SortField, dir: SortDir) => void;
  onChangePage: (page: number) => void;
  onDelete: (id: number) => void;
  isDeleting: boolean;
  onPreview: (row: AdminInformationItem) => void;
};

const formatWarekiDate = (dateStr: string | null): string => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return '';
  const era = convertWarekiDetail(y, m, d);
  if (!era) return '';
  return `${era}年${String(m).padStart(2, '0')}月${String(d).padStart(2, '0')}日`;
};

const formatWarekiDateTime = (dateTimeStr: string | null): string => {
  if (!dateTimeStr) return '';
  const [datePart, timePart] = dateTimeStr.split(/T| /);
  if (!datePart) return '';
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return '';
  const era = convertWarekiDetail(y, m, d);
  if (!era) return '';
  const formattedDate = `${era}年${String(m).padStart(2, '0')}月${String(d).padStart(2, '0')}日`;
  const formattedTime = timePart ? timePart.substring(0, 5) : '';
  return formattedTime ? `${formattedDate} ${formattedTime}` : formattedDate;
};

const renderTenantNames = (names: string[], clinicIds: number[]) => {
  const parts: string[] = [...names];
  if (clinicIds.length > 0) {
    parts.push('個別治療院');
  }
  if (!parts.length) return '';
  if (parts.length <= 2) return parts.join(', ');
  const visible = parts.slice(0, 2).join(', ');
  const overflow = parts.slice(2).join(', ');
  return (
    <AppTooltip title={overflow}>
      <span>
        {visible}… (+{parts.length - 2})
      </span>
    </AppTooltip>
  );
};

const InformationsTableSection: React.FC<InformationsTableSectionProps> = ({
  showSiteTypeColumn,
  rows,
  loading,
  meta,
  defaultPerPage,
  onSort,
  onChangePage,
  onDelete,
  isDeleting,
  onPreview,
}) => {
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { colors } = useTheme();
  const openDeleteConfirm = useStandardDeleteConfirm();

  const openPreview = (row: AdminInformationItem) => {
    onPreview(row);
  };

  const handleDelete = (id: number) => {
    openDeleteConfirm({
      okButtonProps: { loading: isDeleting },
      onOk: async () => {
        onDelete(id);
      },
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: true,
    },
    ...(showSiteTypeColumn
      ? [
          {
            title: '表示対象',
            key: 'tenant_names',
            width: 160,
            render: (_: unknown, row: AdminInformationItem) =>
              renderTenantNames(row.tenant_names, row.clinic_ids),
          },
        ]
      : []),
    {
      title: 'タイトル',
      dataIndex: 'title',
      key: 'title',
      render: (title: string | null) => {
        const plainText = title ? title.replace(/<[^>]*>/g, '') : '';
        return renderTableCellTruncate(plainText, {
          className: 'break-words',
        });
      },
    },
    {
      title: 'お知らせ日',
      dataIndex: 'information_date',
      key: 'information_date',
      width: 180,
      sorter: true,
      render: (_: unknown, row: AdminInformationItem) => formatWarekiDate(row.information_date),
    },
    {
      title: '有効期限',
      key: 'expiration_date',
      width: 220,
      render: (_: unknown, row: AdminInformationItem) => {
        const formattedFrom = formatWarekiDateTime(row.display_from);
        const formattedTo = formatWarekiDateTime(row.display_to);
        if (!formattedFrom && !formattedTo) return null;
        return (
          <div className="whitespace-pre-line leading-relaxed">
            {formattedFrom ? `${formattedFrom}～` : '～'}
            {'\n'}
            {formattedTo ? formattedTo : ''}
          </div>
        );
      },
    },
    {
      title: '表示サービス',
      key: 'service_type',
      width: 180,
      render: (_: unknown, row: AdminInformationItem) => getServiceTypeShortLabel(row.service_type),
    },
    {
      title: '操作',
      key: 'actions',
      width: 260,
      render: (_: unknown, row: AdminInformationItem) => (
        <div className="flex gap-2">
          <AppButton type="primary" onClick={() => openPreview(row)}>
            プレビュー
          </AppButton>
          <AppButton
            style={
              {
                backgroundColor: colors.secondary,
                ['--action-bg-hover' as string]: colors.secondaryHover,
              } as React.CSSProperties
            }
            className="hover:!bg-[var(--action-bg-hover)]"
            icon={<EditFill />}
            type="primary"
            onClick={() => {
              queryClient.removeQueries({ queryKey: ['informations', currentSite, row.id] });
              navigate(ADMIN_ROUTES.INFORMATIONS.EDIT(currentSite, row.id));
            }}
          >
            編集
          </AppButton>
          <AppButton
            icon={<Delete />}
            ghost
            type="primary"
            className="ant-btn-dangerous ant-btn-color-dangerous"
            onClick={() => handleDelete(row.id)}
          >
            削除
          </AppButton>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-md border">
      <AppTable
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        onChange={(_pagination, _filters, sorter) => {
          const sort = Array.isArray(sorter) ? sorter[0] : sorter;
          if (sort && sort.order) {
            const fieldMap: Record<string, SortField> = {
              id: 'informations.id',
              information_date: 'informations.information_date',
            };
            const mapped = fieldMap[sort.field as string];
            if (mapped) {
              onSort(mapped, sort.order as SortDir);
            }
          }
        }}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT_FILTERED} />
          ),
        }}
      />

      {meta && (
        <div className="p-4 flex justify-end">
          <AppPagination
            current={meta.current_page}
            total={meta.total}
            pageSize={meta.per_page || defaultPerPage}
            hideOnSinglePage
            onChange={p => onChangePage(p)}
          />
        </div>
      )}
    </div>
  );
};

export default InformationsTableSection;
